import type { WeatherData } from "../types";

const SKY_ICONS: Record<string, string> = {
  Clear: "\u2600\uFE0F",
  "Partly Cloudy": "\u26C5",
  "Mostly Cloudy": "\u2601\uFE0F",
  Overcast: "\u2601\uFE0F",
  Rain: "\uD83C\uDF27\uFE0F",
};

interface Props {
  weather: WeatherData;
  compact?: boolean;
}

/** Map a temperature value to a color along the cold-blue / warm-green / hot-orange spectrum */
function tempColor(value: number | null, unit: string): string {
  if (value == null) return "#8888aa";
  // Normalize to Fahrenheit for consistent thresholds
  const f = unit === "C" ? value * 1.8 + 32 : value;
  if (f <= 50) return "#4da6ff"; // cold blue
  if (f <= 65) return "#3bc9db"; // cool teal
  if (f <= 80) return "var(--live)"; // warm green (accent)
  if (f <= 100) return "#ffaa22"; // hot orange
  return "#ff5544"; // extreme red-orange
}

export default function WeatherBadge({ weather, compact }: Props) {
  const unit = weather.units === "metric" ? "C" : "F";
  const windUnit = weather.units === "metric" ? "kph" : "mph";
  const skyIcon = weather.skies ? SKY_ICONS[weather.skies] || "" : "";
  const airColor = tempColor(weather.airTemp, unit);
  const trkColor = tempColor(weather.trackTemp, unit);

  /* Compact: pill badge. Native title keeps detail without extra hover DOM. */
  if (compact) {
    return (
      <div
        className="wb-pill"
        title={`${weather.skies || "—"} · ${weather.humidity?.toFixed(0) || "-"}% · Wind ${weather.windVel?.toFixed(0) || "-"} ${windUnit}`}
      >
        <span className="wb-pill__icon">{skyIcon}</span>
        <span className="wb-pill__temps">
          <span style={{ color: airColor }}>{weather.airTemp?.toFixed(0) || "-"}°</span>
          <span className="wb-pill__sep">/</span>
          <span style={{ color: trkColor }}>{weather.trackTemp?.toFixed(0) || "-"}°</span>
          <span className="wb-pill__unit">{unit}</span>
        </span>
      </div>
    );
  }

  /* ── Full: weather station readout card ──────────────────────────── */
  return (
    <div className="wb-card">
      {/* Sky icon column */}
      <div className="wb-card__icon">
        <span>{skyIcon}</span>
        {weather.skies && <span className="wb-card__sky-label">{weather.skies}</span>}
      </div>

      {/* Primary readout: air + track */}
      <div className="wb-card__primary">
        <div className="wb-card__gauge">
          <span className="wb-card__gauge-label">AIR</span>
          <span className="wb-card__gauge-value" style={{ color: airColor }}>
            {weather.airTemp?.toFixed(1) || "-"}
          </span>
          <span className="wb-card__gauge-unit">°{unit}</span>
        </div>
        <div className="wb-card__divider" />
        <div className="wb-card__gauge">
          <span className="wb-card__gauge-label">TRK</span>
          <span className="wb-card__gauge-value" style={{ color: trkColor }}>
            {weather.trackTemp?.toFixed(1) || "-"}
          </span>
          <span className="wb-card__gauge-unit">°{unit}</span>
        </div>
      </div>

      {/* Secondary readout strip */}
      <div className="wb-card__secondary">
        <div className="wb-card__metric">
          <span className="wb-card__metric-label">HUM</span>
          <span className="wb-card__metric-value">{weather.humidity?.toFixed(0) || "-"}%</span>
        </div>
        <div className="wb-card__metric">
          <span className="wb-card__metric-label">WIND</span>
          <span className="wb-card__metric-value">
            {weather.windVel?.toFixed(0) || "-"} {windUnit}
          </span>
        </div>
        {weather.trackState && (
          <div className="wb-card__metric">
            <span className="wb-card__metric-label">SURF</span>
            <span className="wb-card__metric-value">{weather.trackState}</span>
          </div>
        )}
      </div>
    </div>
  );
}
